import { useNavigate, useSearchParams } from "react-router-dom";
import { AppBar } from "@/components/AppBar";
import { IdentityCard } from "@/components/identity/IdentityCard";
import { StepIndicator } from "@/components/StepIndicator";
import { IDENTITY_CARDS } from "@/data/identities";
import { useContact } from "@/context/ContactContext";
import { createIntakeContext } from "@/lib/intakeContext";
import type { IdentityId, SchoolId } from "@/types/navigation";
import { isSchoolId } from "@/types/navigation";

export function IdentityPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { openIntakeContact } = useContact();
  const schoolParam = params.get("school");
  const school: SchoolId = isSchoolId(schoolParam) ? schoolParam : "nus";

  const pick = (identity: IdentityId) => {
    if (identity === "other") {
      openIntakeContact(
        createIntakeContext({ school, source: "other" }),
      );
      return;
    }
    navigate(`/guide?school=${school}&identity=${identity}`);
  };

  return (
    <>
      <AppBar title="选择身份" showBack />
      <StepIndicator current={2} />
      <main className="pb-7">
        <div className="px-5 pb-6 pt-4">
          <h1 className="text-[30px] font-bold leading-tight tracking-tight text-ink-900">
            你是哪种情况?
            <br />
            <span className="text-coral-700">我们帮你对上号。</span>
          </h1>
          <p className="mt-3.5 text-sm leading-[1.7] text-ink-600">
            住房没有标准答案。先告诉我们你的处境——后面的内容会按你的情况来组织。
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 px-4">
          {IDENTITY_CARDS.map((card, i) => (
            <IdentityCard
              key={card.id}
              card={card}
              index={i}
              onSelect={() => pick(card.id)}
            />
          ))}
        </div>

        <div className="px-4 pt-4">
          <button
            type="button"
            onClick={() => pick("other")}
            className="flex w-full items-center gap-3.5 rounded-xl border border-dashed border-ink-300 px-[18px] py-[18px] text-left active:scale-[0.99]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-100 text-ink-600">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <circle cx="5" cy="9" r="1.2" fill="currentColor" />
                <circle cx="9" cy="9" r="1.2" fill="currentColor" />
                <circle cx="13" cy="9" r="1.2" fill="currentColor" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-serif text-[15px] font-medium text-ink-900">
                我的情况不太一样
              </p>
              <p className="mt-0.5 text-xs text-ink-400">直接跟管家聊,我们帮你拆解</p>
            </div>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-ink-300" aria-hidden>
              <path
                d="M5 3l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <p className="mt-8 border-t border-[var(--line)] px-5 pt-4 text-center text-xs leading-[1.7] text-ink-400">
          没有&quot;最好&quot;的答案,只有&quot;最像你&quot;的那个。
          <br />
          选错了也没关系,后面随时可以改。
        </p>
      </main>
    </>
  );
}
